/* eslint-disable react-hooks/exhaustive-deps */
import { FormControl, FormHelperText, InputLabel, TextField, Snackbar } from "@material-ui/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import apiClient from "../../../lib/common/apiClient";

import IconButton from "../../input/IconButton";
import Icon from "../../presentation/Icon";

import InputAdornment from "@mui/material/InputAdornment";
import { BsQrCodeScan } from "react-icons/bs";

import uiConf from "../../../config/uiConf";
import { Alert } from "@material-ui/lab";

const Hangul = require("hangul-js");

/**
 * @COMPONENT_NAME FormItemBarcodeScan
 * @DESCRIPTION 바코드 스캔 입력 폼 컴포넌트. 바코드를 입력받아 자동으로 바코드 정보를 조회하고
 *              결과를 콜백으로 전달합니다. 한글 입력을 영문으로 변환하는 기능과 바코드 스캔 API 
 *              호출을 통한 자동 완성 기능을 제공합니다.
 *
 * @PASSTHROUGH_PROPS
 *   - {String} id - 입력 필드의 고유 식별자
 *   - {String} className - 추가 CSS 클래스명
 *   - {String} label - 입력 필드 라벨 텍스트
 *   - {Object} InputLabelProps - MUI InputLabel에 전달될 추가 props
 *   - {String} helperText - 입력 필드 하단에 표시될 도움말 텍스트
 *   - {Object} column - 폼 컬럼 설정 객체 (barcodeScanTarget, required 등 포함)
 *   - {String} value - 입력 필드의 초기값
 *   - {Object} focusRef - React ref 객체 (입력 필드 포커스 제어용)
 *   - {Object} focusRefCurrent - focusRef.current 객체
 *   - {Object} others - TextField에 전달될 기타 MUI props
 *
 * @CALLBACKS
 *   - {Function} onChange - 바코드 값 변경 시 호출될 콜백 함수
 *   - {Function} barcodeCallback - 바코드 스캔 성공 시 호출될 콜백 함수
 */
function FormItemBarcodeScan(props) {
    const { t } = useTranslation();

    const [barcode, setBarcode] = useState("");
    const [barcodeApply, setBarcodeApply] = useState(true);

    const {
        id,
        className,
        label,
        InputLabelProps,
        helperText,

        column,
        value,
        onChange,

        barcodeCallback,

        focusRef,
        focusRefCurrent,
        ...others
    } = props;

    useEffect(() => {
        setBarcode(value);
    }, [value]);

    let barcodeProps = {
        InputLabelProps: {
            shrink: true,
            ...InputLabelProps,
        },
        onChange: (event) => {
            setBarcode(event.target.value);
        },
        ...others,
    };

    const [notification, setNotification] = useState({
        open: false,
        severity: "success",
        msg: "",
    });

    if (column.InputLabelProps) {
        barcodeProps.InputLabelProps = { ...barcodeProps.InputLabelProps, ...column.InputLabelProps };
    }

    if (column.inputProps) {
        barcodeProps.inputProps = column.inputProps;
    }

    if (column.style) {
        barcodeProps.style = column.style;
    }

    const barcodeConverter = (input) => {
        const koreanToEnglishMap = {
            ㅂ: "Q",
            ㅈ: "W",
            ㄷ: "E",
            ㄱ: "R",
            ㅅ: "T",
            ㅛ: "Y",
            ㅕ: "U",
            ㅑ: "I",
            ㅐ: "O",
            ㅔ: "P",
            ㅁ: "A",
            ㄴ: "S",
            ㅇ: "D",
            ㄹ: "F",
            ㅎ: "G",
            ㅗ: "H",
            ㅓ: "J",
            ㅏ: "K",
            ㅣ: "L",
            ㅋ: "Z",
            ㅌ: "X",
            ㅊ: "C",
            ㅍ: "V",
            ㅠ: "B",
            ㅜ: "N",
            ㅡ: "M",
            ㄲ: "R",
            ㄸ: "E",
            ㅃ: "Q",
            ㅆ: "T",
            ㅉ: "W",
            ㅒ: "O",
            ㅖ: "P",
        };

        return [...input].map((char) => koreanToEnglishMap[char] || char).join("");
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            onChange(barcode);
        }, 10);

        return () => clearTimeout(delayDebounceFn);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [barcode]);

    useEffect(() => {
        const timerId = setTimeout(() => {
            if (!barcodeApply) return;

            if (barcode.length > 0) {
                const convertedInput = barcodeConverter(Hangul.disassemble(barcode).join(""));

                if (convertedInput !== barcode) {
                    setBarcode(convertedInput);
                    return;
                }

                if (column.barcodeScanTarget) {
                    const validTargets = ["lot", "worker", "machine"];

                    if (!validTargets.includes(column.barcodeScanTarget)) {
                        setNotification({
                            severity: "error",
                            msg: t("error.invalidBarcodeSchema"),
                            open: true,
                        });
                        return;
                    }

                    const url = `/api/BarcodeScan/${column.barcodeScanTarget}/${barcode}`;

                    apiClient
                        .get(url, {
                            headers: {
                                "Content-Type": "application/json",
                            },
                        })
                        .then((result) => {
                            onApiSuccess(result);
                        })
                        .catch((reason) => {
                            onApiFailure(reason);
                        });
                }
            }
        }, 300);

        return () => {
            clearTimeout(timerId);
        };
    }, [barcode]);

    const onApiSuccess = (result) => {
        const resultData = result.data.data;
        if (barcodeCallback) barcodeCallback(resultData);
    };

    const onApiFailure = (reason) => {
        notifyFailure(t(`error.invalidBarcode`));
    };

    const notifyFailure = (msg) => {
        setNotification({
            severity: "error",
            msg: msg,
            open: true,
        });
    };

    const onCloseNotification = () => {
        setNotification((notification) => {
            return { ...notification, open: false };
        });
    };

    const textClearClick = (e) => {
        focusRefCurrent?.focus();
        setBarcode("");
        setBarcodeApply(true);
    };

    return (
        <FormControl className={className} required={column.required} {...others}>
            <InputLabel id={id + "-label"} shrink={true} {...InputLabelProps}>
                {label}
            </InputLabel>

            <TextField
                inputRef={focusRef}
                value={barcode}
                labelId={id + "-label"}
                id={id}
                style={{ paddingTop: "16px" }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <BsQrCodeScan />
                        </InputAdornment>
                    ),
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton onClick={textClearClick} disabled={barcodeProps.disabled}>
                                <Icon>close</Icon>
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                {...barcodeProps}
            />

            <FormHelperText>{helperText}</FormHelperText>

            <Snackbar
                open={notification.open && notification.severity !== "success"}
                autoHideDuration={uiConf.dialogNotifyAutoHideDuration}
                onClose={onCloseNotification}
            >
                <Alert onClose={onCloseNotification} variant="filled" severity={notification.severity}>
                    {notification.msg}
                </Alert>
            </Snackbar>
        </FormControl>
    );
}

export default FormItemBarcodeScan;
